import { useCallback, useEffect, useRef, useState } from 'react';

import { logReadFailure } from '../lib/net/logReadFailure';
import { useOnMissionsChanged } from '../lib/mission/missionsChanged';
import { supabase } from '../lib/supabase';

/** What a sheet's « Missions liées » are read for. */
export type MissionEntityKind = 'account' | 'contact' | 'opportunity';

/** A mission of an account, a contact or a deal, as `get_entity_missions` returns it (pass-through). */
export interface EntityMission {
  thread_id: string;
  title: string;
  status: string;
  source: string;
  source_id: string;
  next_wakeup_at: string | null;
  updated_at: string;
}

/**
 * The open missions of an account (its own and those reached through its contacts, opportunities,
 * mails), of a contact or of a deal — one reader for every surface (`get_entity_missions`,
 * PLAN-DETTE 4.43).
 * Re-read when a mission is opened from the app (`notifyMissionsChanged`).
 */
export function useEntityMissions(kind: MissionEntityKind, id: string | null) {
  const [missions, setMissions] = useState<EntityMission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The sheet shown now: an answer for the previous one (a quick switch) is dropped.
  const current = useRef(id);
  current.current = id;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data, error: rpcError } = await supabase.rpc('get_entity_missions', { p_kind: kind, p_id: id });
    if (current.current !== id) return;
    setLoading(false);
    if (rpcError) {
      logReadFailure('travail', 'entity missions failed', rpcError, { kind, id });
      setError('Les missions liées n’ont pas pu être lues.');
      return;
    }
    setError(null);
    setMissions(data ?? []);
  }, [kind, id]);

  useEffect(() => {
    setMissions([]);
    void load();
  }, [load]);

  useOnMissionsChanged(() => void load());

  return { missions, loading, error };
}
